import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  DeleteOutline as DeleteIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  clientDashboardService,
  ClientTeamUser,
} from '../../services/clientDashboardService';

/**
 * Client-portal "Mon équipe" tab.
 * Lets a client create and manage several login users (sub-accounts) that all
 * access the same client account, each with their own credentials.
 */
const ClientTeamTab: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<ClientTeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });

  const [confirmDelete, setConfirmDelete] = useState<ClientTeamUser | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await clientDashboardService.getClientTeam();
      setUsers(res.users || []);
    } catch (e: any) {
      setError(e?.message || 'Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({ firstName: '', lastName: '', email: '', password: '' });
    setShowPassword(false);
    setError(null);
    setDialogOpen(true);
  };

  const validate = (): string | null => {
    if (!form.firstName.trim()) return 'Le prénom est obligatoire';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Veuillez saisir un email valide';
    if (!form.password || form.password.length < 6) return 'Le mot de passe doit contenir au moins 6 caractères';
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await clientDashboardService.createClientTeamUser({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      setSuccess('Utilisateur créé avec succès');
      setDialogOpen(false);
      load();
    } catch (e: any) {
      setError(e?.message || "Erreur lors de la création de l'utilisateur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u: ClientTeamUser) => {
    setError(null);
    try {
      await clientDashboardService.deactivateClientTeamUser(u.id);
      setSuccess('Utilisateur désactivé');
      setConfirmDelete(null);
      load();
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de la désactivation');
      setConfirmDelete(null);
    }
  };

  const currentEmail = (currentUser as any)?.email;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <GroupIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Mon équipe
          </Typography>
        </Stack>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={openCreate}>
          Ajouter un utilisateur
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Créez plusieurs comptes pour votre organisation. Chaque utilisateur se
        connecte avec ses propres identifiants et accède aux mêmes données que
        votre compte client.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card elevation={2}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ p: 4 }}>
              <Typography color="text.secondary">Aucun utilisateur pour le moment.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nom</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Dernière connexion</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u) => {
                    const isSelf = currentEmail && u.email === currentEmail;
                    return (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          {u.first_name} {u.last_name}
                          {isSelf && (
                            <Chip size="small" label="Vous" sx={{ ml: 1 }} variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={u.is_active ? 'success' : 'default'}
                            label={u.is_active ? 'Actif' : 'Inactif'}
                          />
                        </TableCell>
                        <TableCell>
                          {u.last_login_at
                            ? new Date(u.last_login_at).toLocaleString('fr-FR')
                            : 'Jamais'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={isSelf ? 'Vous ne pouvez pas vous désactiver' : 'Désactiver'}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={!!isSelf}
                                onClick={() => setConfirmDelete(u)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un utilisateur</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prénom"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                helperText="Au moins 6 caractères"
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? 'Création...' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm deactivate */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Désactiver l'utilisateur</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir désactiver l'accès de{' '}
            <strong>
              {confirmDelete?.first_name} {confirmDelete?.last_name}
            </strong>{' '}
            ? Cet utilisateur ne pourra plus se connecter.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={() => confirmDelete && remove(confirmDelete)}>
            Désactiver
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientTeamTab;
